/**
 * 「从备份恢复」向导 —— **N 域 N4 §5.4.2 的端侧半边**（批次 `65`）
 *
 * 依据：N 域 PRD **§5.4.2**（恢复）· **§7.4 `AC-N4.16`–`AC-N4.22`** ·
 * `contract/restore.ts` 文件头（**三项计数的口径** + `conflicted ⊆ overwritten`）·
 * 原型 `08-家庭与设置_原型.html` 的「态展示 · 恢复冲突裁决」区（口径以原型为准，铁律 14）·
 * 本批的纯逻辑层 `features/restore.ts`。
 *
 * ## 🔴 内容层**单独导出**（`RestoreDialogBody`）—— 与 `DeletionDialog` 同一课
 *
 * MUI `Dialog` 走 **Portal** ⇒ 子树被挂到 `document.body`，而端侧测试基座是
 * `renderToStaticMarkup`（**无 DOM**）⇒ 直接断言"对话框内容在不在产物里"会得到
 * **假红 / 假绿两种不可靠结果**（㉝ / `60` / `63` 批反复踩）。⇒ 内容层**全受控 + 单独导出**，
 * 外壳只管 `open` / 取数 / 提交。并配**结构断言**钉住"外壳在静态渲染下不产出内容"。
 *
 * ## 🔴 五条 UI 纪律（每条都有断言看着）
 *
 * | # | 纪律 | 怎么做的 |
 * |---|---|---|
 * | ① | 🔴 **前提取值只读 `requires`**（`AC-N4.19` / `N4.20` / `N-D2`） | 服务端说"要"才渲染那一段，说"不要"就**不渲染**；端侧**没有** `mode === 'overwrite'` 这种判断 |
 * | ② | 🔴 **冲突逐项可裁决**（`AC-N4.17` · **必测**） | 每条冲突一行 `segmented` 三选一（原型逐字）；批量按钮只对**已列出的**冲突生效 |
 * | ③ | 🔴 **`merge` 报丢弃清单**（`AC-N4.18` · **必测**） | 结果区逐条列出 `discards` + 原因；`discardsTruncated` 为真时**必须**说"另有 N 条" |
 * | ④ | 🔴 **截断必须明示** | 冲突与丢弃两处都有 `*Truncated` 旗标 ⇒ 静默截断 = 让用户以为"就这么多" |
 * | ⑤ | 🔴 **完整性三项非 0 要显眼**（`AC-N4.22` 运行期半边） | 非 0 时用 `warning` 而不是 `success`，且**不**说"恢复成功"这种笼统话 |
 *
 * ## ⚠️ 本批**不做**（诚实登记 · 见 changelog）
 *
 * - 🔴 **`AC-N4.21`（覆盖前自动快照 + 用户可见可回滚）** —— 需**新载体**（36 张表里没有它的位置）
 *   ⇒ 先走 **§6.1 `#27`** 的 DR。本批的兜底 = **强制前置导出**（`N-O5`，服务端逐条校验）；
 * - ⚠️ **跨账本恢复**（id 重映射 + 成员收养）—— see **§6.1 `#28`**，**不做半吊子**；
 * - ⚠️ **预览阶段的"按分类/账户下钻看差异"** —— 契约层刻意只下发**变化列**（整行会撑到与备份同量级）；
 * - ⚠️ **小程序端入口** —— 归 M 域（小程序端仍是空壳）。
 */

import { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import {
  RESTORE_COUNT_LABELS,
  RESTORE_DISCARD_REASON_LABELS,
  RESTORE_MODE_LABELS,
  RESTORE_MODE_NOTES,
  RESTORE_RESOLUTION_LABELS,
  RESTORE_WARNING_LABELS,
  type RestoreConflictView,
  type RestoreMode,
  type RestorePreviewView,
  type RestoreResolution,
  type RestoreResultView,
} from '@jiaying/shared';
import { previewRestore, runRestore } from '../api/endpoints.js';
import { ApiError } from '../api/http.js';
import {
  backupOriginText,
  buildRestoreRequest,
  conflictCountText,
  conflictFactText,
  decidedCount,
  decisionKeyOf,
  readBackupText,
  resolutionOf,
  withAllResolutions,
  withResolution,
  writtenText,
  integrityText,
  overwriteImpactText,
  type DecisionsDraft,
} from '../features/restore.js';
import { RESTORE_ACTION, RESTORE_TITLE } from '../features/sovereignty-labels.js';

// ═══════════════════════ 文案（本文件的唯一来源） ═══════════════════════

const STAGE_TITLE = { pick: '第 1 步 · 选择备份文件', preview: '第 2 步 · 恢复预览', done: '恢复结果' } as const;
const PICK_HINT = '选择一份由家盈导出的 `.json` 备份文件。恢复前会先给你一份预览报告，不会直接动数据。';
const PICK_ADVICE = '建议先把当前数据导出一份（设置页「数据主权」→ 导出完整备份）—— 恢复是不可逆的。';
const PREVIEW_NOTE = '以下数字来自服务端逐行比对，恢复前不会改动任何数据。';
const CONFLICT_TITLE = '冲突项（逐项裁决）';
const CONFLICT_HINT =
  '「保留本地」= 丢弃备份里这一行；「采用备份」= 用备份值改写；「跳过」= 这次不动（不计入丢弃清单）。';
const DISCARD_TITLE = '会被丢弃的备份行（合并模式）';
const NOT_WRITABLE_TITLE = '备份里有、但恢复不写回的表';
const WARN_TITLE = '预览警告';
const MODE_TITLE = '恢复模式';
const RUN_MERGE = '按以上裁决执行（合并模式）';
const RUN_OVERWRITE = '按以上裁决执行（覆盖模式）';
const ALL_KEEP_LOCAL = '全部保留本地';
const ALL_USE_BACKUP = '全部采用备份';
const STEP_EXPORT = '第 3 步 · 前置导出确认';
const EXPORT_LABEL = '我已导出当前账本的备份（覆盖模式会先清空，导出的那份是唯一的退路）';
const STEP_CONFIRM = '第 4 步 · 二次输入账本名';
const STEP_MISSING_ACK = '确认继续（备份缺少关键表）';
const CLOSE = '关闭';
const CANCEL = '取消';
const RESULT_TITLE = '已执行';
const RESULT_BALANCE_NOTE =
  '以上是恢复后服务端算出的逐账户余额 —— 与你导出时的那份对不上，就说明恢复结果有问题。';

// ─────────────────────────── 子件：冲突行 ───────────────────────────

/** 一条冲突 —— 🔴 **受控**（父级持裁决草稿），三选一 segmented（原型逐字） */
export function RestoreConflictRow({
  conflict,
  resolution,
  onResolutionChange,
}: {
  conflict: RestoreConflictView;
  resolution: RestoreResolution;
  onResolutionChange: (next: RestoreResolution) => void;
}) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      sx={{ py: 1 }}
      data-testid={`restore-conflict-${conflict.table}-${conflict.key}`}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} data-testid="restore-conflict-key">
          {conflict.table} · {conflict.key}
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div" data-testid="restore-conflict-fact">
          {conflictFactText(conflict.changedColumns, conflict.localValues, conflict.backupValues)}
        </Typography>
      </Box>
      <Stack direction="row" spacing={0.5}>
        {(['keepLocal', 'useBackup', 'skip'] as const).map((r) => (
          <Button
            key={r}
            size="small"
            variant={resolution === r ? 'contained' : 'outlined'}
            onClick={() => {
              onResolutionChange(r);
            }}
            data-testid={`restore-resolution-${r}`}
          >
            {RESTORE_RESOLUTION_LABELS[r]}
          </Button>
        ))}
      </Stack>
    </Stack>
  );
}

// ─────────────────────────── 内容层 ───────────────────────────

/** 内容层 props —— 🔴 **全受控**（父级持全部状态），这样它才能被静态渲染精确断言 */
export interface RestoreDialogBodyProps {
  /** 当前阶段 */
  stage: 'pick' | 'preview' | 'done';
  /** 文件解析失败的原因（`null` = 没失败） */
  fileError: string | null;
  /** 正在读文件 */
  reading: boolean;
  /** 预览（`stage === 'preview'` 且非 `null`） */
  preview: RestorePreviewView | null;
  /** 恢复结果（`stage === 'done'`） */
  result: RestoreResultView | null;
  /** 当前选中的模式 */
  mode: RestoreMode;
  onModeChange: (next: RestoreMode) => void;
  /** 裁决草稿 */
  decisions: DecisionsDraft;
  onResolution: (table: string, key: string, next: RestoreResolution) => void;
  onAllResolutions: (next: RestoreResolution) => void;
  /** 前置条件 */
  exportedBeforehand: boolean;
  onExportedBeforehandChange: (next: boolean) => void;
  ackMissingTables: boolean;
  onAckMissingTablesChange: (next: boolean) => void;
  confirmLedgerName: string;
  onConfirmLedgerNameChange: (next: string) => void;
  /** 提交中 / 提交失败 */
  busy: boolean;
  error: string | null;
  /** 用户选了文件 */
  onPickFile: () => void;
  onRun: () => void;
  onClose: () => void;
  /** 前置条件是否满足（🔴 **由父级用 `isPrerequisiteSatisfied` 算** —— 本件不算） */
  canRun: boolean;
}

export function RestoreDialogBody(props: RestoreDialogBodyProps) {
  const { preview } = props;

  return (
    <Box sx={{ p: 3 }} data-testid="restore-dialog-body">
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {RESTORE_TITLE}
      </Typography>

      {/* ═══════════ 阶段一：选文件 ═══════════ */}
      {props.stage === 'pick' ? (
        <Box sx={{ mt: 1 }} data-testid="restore-stage-pick">
          <Typography variant="caption" color="text.secondary" component="div">
            {STAGE_TITLE.pick}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {PICK_HINT}
          </Typography>
          <Alert severity="info" sx={{ mt: 1.5 }} data-testid="restore-pick-advice">
            {PICK_ADVICE}
          </Alert>
          {props.fileError !== null ? (
            <Alert severity="error" sx={{ mt: 1.5 }} data-testid="restore-file-error">
              {props.fileError}
            </Alert>
          ) : null}
          {props.reading ? <LinearProgress sx={{ mt: 1.5 }} data-testid="restore-reading" /> : null}
          <Stack direction="row" spacing={1} sx={{ mt: 2 }} justifyContent="flex-end">
            <Button size="small" variant="outlined" onClick={props.onClose} disabled={props.busy}>
              {CANCEL}
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={props.onPickFile}
              disabled={props.reading || props.busy}
              data-testid="restore-pick-file"
            >
              {RESTORE_ACTION}
            </Button>
          </Stack>
        </Box>
      ) : null}

      {/* ═══════════ 阶段二：预览 ═══════════ */}
      {props.stage === 'preview' && preview !== null ? (
        <Box sx={{ mt: 1 }} data-testid="restore-stage-preview">
          <Typography variant="caption" color="text.secondary" component="div">
            {STAGE_TITLE.preview}
          </Typography>

          {/* ── 三项计数（🔴 口径见 `contract/restore.ts` 文件头）── */}
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            <Chip size="small" color="success" variant="outlined" data-testid="restore-count-added"
              label={`${RESTORE_COUNT_LABELS.added} ${String(preview.counts.added)} 笔`} />
            <Chip size="small" variant="outlined" data-testid="restore-count-overwritten"
              label={`${RESTORE_COUNT_LABELS.overwritten} ${String(preview.counts.overwritten)} 笔`} />
            <Chip size="small" variant="outlined" color="warning" data-testid="restore-count-conflicted"
              label={`${RESTORE_COUNT_LABELS.conflicted} ${String(preview.counts.conflicted)} 笔`} />
          </Stack>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }} data-testid="restore-origin">
            {backupOriginText(preview)}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div" data-testid="restore-preview-note">
            {PREVIEW_NOTE}
          </Typography>

          {/* ── 警告（🔴 判据是 `code`，不是 `message`）── */}
          {preview.warnings.length > 0 ? (
            <Box sx={{ mt: 1.5 }} data-testid="restore-warnings">
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {WARN_TITLE}
              </Typography>
              {preview.warnings.map((w) => (
                <Alert key={w.code} severity="warning" sx={{ mt: 0.5 }} data-testid={`restore-warning-${w.code}`}>
                  <strong>{RESTORE_WARNING_LABELS[w.code]}</strong>：{w.message}
                </Alert>
              ))}
            </Box>
          ) : null}

          <Divider sx={{ my: 1.5 }} />

          {/* ── 模式（🔴 两种都列，让用户在同一屏比较）── */}
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {MODE_TITLE}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
            {(['merge', 'overwrite'] as const).map((m) => (
              <Button
                key={m}
                size="small"
                variant={props.mode === m ? 'contained' : 'outlined'}
                onClick={() => {
                  props.onModeChange(m);
                }}
                data-testid={`restore-mode-${m}`}
              >
                {RESTORE_MODE_LABELS[m]}
              </Button>
            ))}
          </Stack>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }} data-testid="restore-mode-note">
            {RESTORE_MODE_NOTES[props.mode]}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div" data-testid="restore-overwrite-impact">
            {overwriteImpactText(preview)}
          </Typography>

          {/* ── 冲突逐项裁决（`AC-N4.17` · 必测）── */}
          <Divider sx={{ my: 1.5 }} />
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {CONFLICT_TITLE}
            </Typography>
            <Chip size="small" variant="outlined" data-testid="restore-conflict-count"
              label={conflictCountText(preview)} />
          </Stack>
          <Typography variant="caption" color="text.secondary" component="div" data-testid="restore-conflict-hint">
            {CONFLICT_HINT}
          </Typography>
          {preview.conflicts.length > 0 ? (
            <>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }} data-testid="restore-bulk-actions">
                <Button size="small" variant="outlined" data-testid="restore-all-keep-local"
                  onClick={() => { props.onAllResolutions('keepLocal'); }}>
                  {ALL_KEEP_LOCAL}
                </Button>
                <Button size="small" variant="outlined" data-testid="restore-all-use-backup"
                  onClick={() => { props.onAllResolutions('useBackup'); }}>
                  {ALL_USE_BACKUP}
                </Button>
              </Stack>
              <Box sx={{ mt: 0.5 }} data-testid="restore-conflict-list">
                {preview.conflicts.map((c) => (
                  <RestoreConflictRow
                    key={decisionKeyOf(c.table, c.key)}
                    conflict={c}
                    resolution={resolutionOf(props.decisions, c.table, c.key)}
                    onResolutionChange={(next) => {
                      props.onResolution(c.table, c.key, next);
                    }}
                  />
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary" component="div" data-testid="restore-decided-count">
                已裁决 {String(decidedCount(preview, props.decisions))} / {String(preview.conflicts.length)} 笔
              </Typography>
            </>
          ) : (
            <Typography variant="caption" color="text.secondary" component="div" data-testid="restore-no-conflict">
              没有需要裁决的冲突项。
            </Typography>
          )}

          {/* ── 丢弃清单预览（`AC-N4.18`：`merge` 禁止静默丢行）── */}
          {props.mode === 'merge' && preview.notWritable.length > 0 ? (
            <Box sx={{ mt: 1.5 }} data-testid="restore-not-writable">
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {NOT_WRITABLE_TITLE}
              </Typography>
              <List dense disablePadding>
                {preview.notWritable.map((t) => (
                  <ListItem key={t.name} disableGutters alignItems="flex-start" data-testid={`restore-not-writable-${t.name}`}>
                    <ListItemText
                      primary={<Box component="code" sx={{ fontSize: '0.75rem' }}>{t.name}</Box>}
                      secondary={t.reason}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          ) : null}

          {/* ── 🔴 前置条件（**逐条由 `requires` 驱动**）── */}
          <Divider sx={{ my: 1.5 }} />
          {preview.requires.preExport ? (
            <Box data-testid="restore-step-export">
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {STEP_EXPORT}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                <Button
                  size="small"
                  variant={props.exportedBeforehand ? 'contained' : 'outlined'}
                  onClick={() => { props.onExportedBeforehandChange(!props.exportedBeforehand); }}
                  data-testid="restore-exported-toggle"
                >
                  {EXPORT_LABEL}
                </Button>
              </Stack>
            </Box>
          ) : null}
          {preview.requires.confirmLedgerName ? (
            <Box sx={{ mt: 1.5 }} data-testid="restore-step-confirm">
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {STEP_CONFIRM}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                请输入备份里的账本名「{preview.backupLedgerName}」以确认
              </Typography>
              <input
                value={props.confirmLedgerName}
                onChange={(e) => { props.onConfirmLedgerNameChange(e.target.value); }}
                data-testid="restore-confirm-input"
                style={{ marginTop: 6, padding: '6px 8px', width: '100%', maxWidth: 320 }}
              />
            </Box>
          ) : null}
          {preview.requires.missingTablesAck ? (
            <Box sx={{ mt: 1.5 }} data-testid="restore-step-missing-ack">
              <Button
                size="small"
                variant={props.ackMissingTables ? 'contained' : 'outlined'}
                color="warning"
                onClick={() => { props.onAckMissingTablesChange(!props.ackMissingTables); }}
                data-testid="restore-missing-ack-toggle"
              >
                {STEP_MISSING_ACK}
              </Button>
            </Box>
          ) : null}

          {props.error !== null ? (
            <Alert severity="error" sx={{ mt: 1.5 }} data-testid="restore-error">
              {props.error}
            </Alert>
          ) : null}

          <Stack direction="row" spacing={1} sx={{ mt: 2 }} justifyContent="flex-end">
            <Button size="small" variant="outlined" onClick={props.onClose} disabled={props.busy}>
              {CANCEL}
            </Button>
            <Button
              size="small"
              variant="contained"
              color={props.mode === 'overwrite' ? 'error' : 'primary'}
              disabled={!props.canRun || props.busy}
              onClick={props.onRun}
              data-testid="restore-run"
            >
              {props.busy
                ? '恢复中…'
                : props.mode === 'overwrite'
                  ? RUN_OVERWRITE
                  : RUN_MERGE}
            </Button>
          </Stack>
        </Box>
      ) : null}

      {/* ═══════════ 阶段三：结果 ═══════════ */}
      {props.stage === 'done' && props.result !== null ? (
        <Box sx={{ mt: 1 }} data-testid="restore-stage-done">
          <Typography variant="caption" color="text.secondary" component="div">
            {STAGE_TITLE.done}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600 }} data-testid="restore-result-mode">
            {RESULT_TITLE} · {RESTORE_MODE_LABELS[props.result.mode]}
          </Typography>
          <Typography variant="body2" data-testid="restore-result-written">
            {writtenText(props.result.written)}
          </Typography>

          {/* 🔴 完整性三项（`AC-N4.22` 运行期半边）—— 非 0 必须显眼 */}
          {(() => {
            const integ = integrityText(props.result.integrity);
            return (
              <Alert
                severity={integ.ok ? 'success' : 'warning'}
                sx={{ mt: 1.5 }}
                data-testid="restore-integrity"
              >
                {integ.text}
              </Alert>
            );
          })()}

          {/* 逐账户余额 */}
          <Typography variant="caption" sx={{ fontWeight: 600, mt: 1.5, display: 'block' }}>
            恢复后逐账户余额
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div" data-testid="restore-balance-note">
            {RESULT_BALANCE_NOTE}
          </Typography>
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }} data-testid="restore-balances">
            {Object.entries(props.result.accountBalances).map(([id, cents]) => (
              <li key={id}>
                <Typography variant="caption">
                  <Box component="code" sx={{ fontSize: '0.72rem' }}>{id}</Box>：{String(cents)} 分
                </Typography>
              </li>
            ))}
          </Box>

          {/* 🔴 丢弃清单（`AC-N4.18` 必测 —— `merge` 禁止静默丢行） */}
          {props.result.discards.length > 0 ? (
            <Box sx={{ mt: 1.5 }} data-testid="restore-discards">
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {DISCARD_TITLE}（{String(props.result.discards.length)} 条
                {props.result.discardsTruncated ? '，另有更多未列出' : ''}）
              </Typography>
              <List dense disablePadding>
                {props.result.discards.map((d) => (
                  <ListItem key={decisionKeyOf(d.table, d.key)} disableGutters alignItems="flex-start"
                    data-testid={`restore-discard-${d.table}-${d.key}`}>
                    <ListItemText
                      primary={<Box component="code" sx={{ fontSize: '0.75rem' }}>{d.table} · {d.key}</Box>}
                      secondary={RESTORE_DISCARD_REASON_LABELS[d.reason]}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.5 }} data-testid="restore-no-discard">
              没有被丢弃的备份行。
            </Typography>
          )}

          <Stack direction="row" sx={{ mt: 2 }} justifyContent="flex-end">
            <Button size="small" variant="contained" onClick={props.onClose} data-testid="restore-done-close">
              {CLOSE}
            </Button>
          </Stack>
        </Box>
      ) : null}
    </Box>
  );
}

// ─────────────────────────── 外壳 ───────────────────────────

/** 外壳 —— 只负责 `open` / 取数 / 提交（内容全在 {@link RestoreDialogBody}） */
export function RestoreDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [stage, setStage] = useState<'pick' | 'preview' | 'done'>('pick');
  const [reading, setReading] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [backup, setBackup] = useState<unknown>(null);
  const [preview, setPreview] = useState<RestorePreviewView | null>(null);
  const [result, setResult] = useState<RestoreResultView | null>(null);
  const [mode, setMode] = useState<RestoreMode>('merge');
  const [decisions, setDecisions] = useState<DecisionsDraft>({});
  const [exportedBeforehand, setExportedBeforehand] = useState(false);
  const [ackMissingTables, setAckMissingTables] = useState(false);
  const [confirmLedgerName, setConfirmLedgerName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toPrereq = (): {
    exportedBeforehand: boolean;
    confirmLedgerName: string;
    acknowledgeMissingTables: boolean;
  } => ({ exportedBeforehand, confirmLedgerName, acknowledgeMissingTables: ackMissingTables });

  /** 选文件 → 读文本 → 解析（🔴 解析规则在纯逻辑层，本件不做判断） */
  const onFile = async (file: File | undefined): Promise<void> => {
    if (file === undefined) return;
    setReading(true);
    setFileError(null);
    setError(null);
    try {
      const text = await file.text();
      const read = readBackupText(text);
      if (!read.ok) {
        setFileError(read.reason);
        return;
      }
      setBackup(read.backup);
      const p = await previewRestore(read.backup);
      setPreview(p);
      setDecisions({});
      setMode('merge');
      setExportedBeforehand(false);
      setAckMissingTables(false);
      setConfirmLedgerName('');
      setStage('preview');
    } catch (e: unknown) {
      // 🔴 原样展示服务端 `message`（含表名/版本的具体诊断，端侧编不出来）
      setFileError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setReading(false);
    }
  };

  const run = async (): Promise<void> => {
    if (preview === null) return;
    setBusy(true);
    setError(null);
    try {
      const body = buildRestoreRequest(preview, { backup, mode, draft: decisions, prereq: toPrereq() });
      const out = await runRestore(body);
      setResult(out);
      setStage('done');
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const prereqSatisfied =
    preview === null
      ? false
      : (!preview.requires.preExport || exportedBeforehand) &&
        (!preview.requires.confirmLedgerName || confirmLedgerName === preview.backupLedgerName) &&
        (!preview.requires.missingTablesAck || ackMissingTables);

  return (
    <>
      <input
        ref={fileRef}
        type="file"
        accept="application/json,.json"
        hidden
        data-testid="restore-file-input"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          void onFile(f);
        }}
      />
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <RestoreDialogBody
          stage={stage}
          fileError={fileError}
          reading={reading}
          preview={preview}
          result={result}
          mode={mode}
          onModeChange={setMode}
          decisions={decisions}
          onResolution={(table, key, next) => {
            setDecisions((prev) => withResolution(prev, table, key, next));
          }}
          onAllResolutions={(next) => {
            setDecisions((prev) => withAllResolutions(prev, preview?.conflicts ?? [], next));
          }}
          exportedBeforehand={exportedBeforehand}
          onExportedBeforehandChange={setExportedBeforehand}
          ackMissingTables={ackMissingTables}
          onAckMissingTablesChange={setAckMissingTables}
          confirmLedgerName={confirmLedgerName}
          onConfirmLedgerNameChange={setConfirmLedgerName}
          busy={busy}
          error={error}
          onPickFile={() => {
            fileRef.current?.click();
          }}
          onRun={() => {
            void run();
          }}
          onClose={onClose}
          canRun={prereqSatisfied}
        />
      </Dialog>
    </>
  );
}
